import React from 'react'
import * as PhosphorIcons from '@phosphor-icons/react'

const InvoiceExtractorViewResult = ({result}) => {
  const getQualityColor = (quality) => {
    if (typeof quality !== 'string') return 'bg-base-200 text-base-content';
    
    switch(quality.toLowerCase()) {
      case 'high':
        return 'bg-success/20 text-success';
      case 'medium':
        return 'bg-warning/20 text-warning';
      case 'low':
        return 'bg-error/20 text-error';
      default:
        return 'bg-base-200 text-base-content';
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    if (typeof amount !== 'number') return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  };

  const invoice = result?.invoice_analysis || {};
  const metadata = result?.metadata || {};

  return (
    <div className="mt-4 overflow-x-auto">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Left Column */}
        <div className="space-y-4">
          {/* Header Info Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-primary/10">
                  <PhosphorIcons.Receipt size={20} weight="bold" className="text-primary"/>
                </div>
                <h3 className="card-title text-lg">Invoice Details</h3>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="p-3 bg-base-200 rounded-lg">
                    <span className="text-sm text-base-content/70">Invoice Number</span>
                    <div className="font-medium mt-1">{invoice?.header_info?.invoice_number || '-'}</div>
                  </div>
                  <div className="p-3 bg-base-200 rounded-lg">
                    <span className="text-sm text-base-content/70">Issue Date</span>
                    <div className="font-medium mt-1">{invoice?.header_info?.issue_date || '-'}</div>
                  </div>
                  <div className="p-3 bg-base-200 rounded-lg">
                    <span className="text-sm text-base-content/70">Due Date</span>
                    <div className="font-medium mt-1">{invoice?.header_info?.due_date || '-'}</div>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="p-3 bg-base-200 rounded-lg">
                    <span className="text-sm text-base-content/70">Total Amount</span>
                    <div className="font-medium mt-1">{formatCurrency(invoice?.header_info?.total_amount, invoice?.header_info?.currency)}</div>
                  </div>
                  <div className="p-3 bg-base-200 rounded-lg">
                    <span className="text-sm text-base-content/70">Status</span>
                    <div className="font-medium mt-1 capitalize">{invoice?.header_info?.payment_status || '-'}</div>
                  </div>
                  <div className="p-3 bg-base-200 rounded-lg">
                    <span className="text-sm text-base-content/70">PO Number</span>
                    <div className="font-medium mt-1">{invoice?.header_info?.purchase_order_number || '-'}</div>
                  </div>
                </div>
              </div>

              {/* Customer Info */}
              {invoice?.header_info?.customer_info && (
                <div className="mt-4">
                  <h4 className="font-medium mb-2">Customer Information</h4>
                  <div className="p-3 bg-base-200 rounded-lg space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <span className="text-sm text-base-content/70">Name</span>
                        <div className="font-medium">{invoice?.header_info?.customer_info?.name}</div>
                      </div>
                      <div>
                        <span className="text-sm text-base-content/70">Company</span>
                        <div className="font-medium">{invoice?.header_info?.customer_info?.company_name}</div>
                      </div>
                    </div>
                    <div>
                      <span className="text-sm text-base-content/70">Address</span>
                      <div className="font-medium">{invoice?.header_info?.customer_info?.address}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <span className="text-sm text-base-content/70">Email</span>
                        <div className="font-medium">{invoice?.header_info?.customer_info?.email}</div>
                      </div>
                      <div>
                        <span className="text-sm text-base-content/70">Phone</span>
                        <div className="font-medium">{invoice?.header_info?.customer_info?.phone}</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Vendor Info */}
              {invoice?.header_info?.vendor_info && (
                <div className="mt-4">
                  <h4 className="font-medium mb-2">Vendor Information</h4>
                  <div className="p-3 bg-base-200 rounded-lg space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <span className="text-sm text-base-content/70">Name</span>
                        <div className="font-medium">{invoice?.header_info?.vendor_info?.name}</div>
                      </div>
                      <div>
                        <span className="text-sm text-base-content/70">Company</span>
                        <div className="font-medium">{invoice?.header_info?.vendor_info?.company_name}</div>
                      </div>
                    </div>
                    <div>
                      <span className="text-sm text-base-content/70">Address</span>
                      <div className="font-medium">{invoice?.header_info?.vendor_info?.address}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <span className="text-sm text-base-content/70">Email</span>
                        <div className="font-medium">{invoice?.header_info?.vendor_info?.email}</div>
                      </div>
                      <div>
                        <span className="text-sm text-base-content/70">Phone</span>
                        <div className="font-medium">{invoice?.header_info?.vendor_info?.phone}</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <span className="text-sm text-base-content/70">Tax ID</span>
                        <div className="font-medium">{invoice?.header_info?.vendor_info?.tax_id}</div>
                      </div>
                      <div>
                        <span className="text-sm text-base-content/70">VAT Number</span>
                        <div className="font-medium">{invoice?.header_info?.vendor_info?.vat_number}</div>
                      </div>
                    </div>
                    {(invoice?.header_info?.vendor_info?.website || invoice?.header_info?.vendor_info?.registration_number) && (
                      <div className="grid grid-cols-2 gap-2">
                        {invoice?.header_info?.vendor_info?.website && (
                          <div>
                            <span className="text-sm text-base-content/70">Website</span>
                            <div className="font-medium">
                              <a href={invoice?.header_info?.vendor_info?.website} 
                                 target="_blank" 
                                 rel="noopener noreferrer"
                                 className="text-primary hover:underline">
                                {invoice?.header_info?.vendor_info?.website}
                              </a>
                            </div>
                          </div>
                        )}
                        {invoice?.header_info?.vendor_info?.registration_number && (
                          <div>
                            <span className="text-sm text-base-content/70">Registration Number</span>
                            <div className="font-medium">{invoice?.header_info?.vendor_info?.registration_number}</div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Line Items Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-secondary/10">
                  <PhosphorIcons.ListBullets size={20} weight="bold" className="text-secondary"/>
                </div>
                <h3 className="card-title text-lg">Line Items</h3>
              </div>
              
              <div className="space-y-3">
                {invoice?.line_items?.map((item, index) => (
                  <div key={index} className="p-3 bg-base-200 rounded-lg">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <span className="font-medium">{item?.description}</span>
                        {item?.item_code && (
                          <div className="text-xs text-base-content/70">Code: {item?.item_code}</div>
                        )}
                      </div>
                      <span className="font-semibold">{formatCurrency(item?.amount)}</span>
                    </div>
                    <div className="text-sm text-base-content/70 flex justify-between">
                      <span>Quantity: {item?.quantity} × Unit Price: {formatCurrency(item?.unit_price)}</span>
                      {item?.tax_rate && (
                        <span>Tax: {item?.tax_rate}%</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Tax Summary */}
              {invoice?.tax_summary && (
                <div className="mt-4 p-3 bg-base-200 rounded-lg">
                  <h4 className="font-medium mb-2">Tax Summary</h4>
                  <div className="space-y-2">
                    {invoice?.tax_summary?.tax_groups?.map((group, index) => (
                      <div key={index} className="flex justify-between text-sm">
                        <span>{group?.description || `Tax ${group?.rate}%`}</span>
                        <span>{formatCurrency(group?.amount)}</span>
                      </div>
                    ))}
                    <div className="border-t border-base-300 pt-2 flex justify-between font-medium">
                      <span>Total Tax</span>
                      <span>{formatCurrency(invoice?.tax_summary?.total_tax_amount)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          {/* Metadata & Quality Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-accent/10">
                  <PhosphorIcons.ChartBar size={20} weight="bold" className="text-accent"/>
                </div>
                <h3 className="card-title text-lg">Extraction Quality</h3>
              </div>

              <div className="space-y-3">
                {/* Confidence Score */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium">Confidence Score</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs bg-success/20 text-success`}>
                      {Math.round((metadata?.confidence_score || 0) * 100)}%
                    </span>
                  </div>
                  <div className="w-full bg-base-300 rounded-full h-2">
                    <div 
                      className="h-2 rounded-full bg-success"
                      style={{width: `${(metadata?.confidence_score || 0) * 100}%`}}
                    ></div>
                  </div>
                </div>

                {/* Extraction Quality */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Extraction Quality</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${getQualityColor(metadata?.extraction_quality)}`}>
                      {metadata?.extraction_quality || 'Unknown'}
                    </span>
                  </div>
                </div>

                {/* Validation Status */}
                {metadata?.validation_status && (
                  <div className="p-3 bg-base-200 rounded-lg">
                    <span className="font-medium mb-2 block">Validation Checks</span>
                    <div className="space-y-2 mt-2">
                      {Object.entries(metadata?.validation_status).map(([key, value]) => (
                        <div key={key} className="flex items-center gap-2">
                          {value ? (
                            <PhosphorIcons.CheckCircle className="text-success" size={16} />
                          ) : (
                            <PhosphorIcons.XCircle className="text-error" size={16} />
                          )}
                          <span className="text-sm capitalize">{key.replace(/_/g, ' ')}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Warnings */}
                {metadata?.warnings?.length > 0 && (
                  <div className="p-3 bg-base-200 rounded-lg">
                    <span className="font-medium mb-2 block">Warnings</span>
                    <div className="space-y-2">
                      {metadata?.warnings?.map((warning, index) => (
                        <div key={index} className="flex items-start gap-2 text-sm">
                          <PhosphorIcons.Warning className="text-warning mt-0.5" size={16} />
                          <div>
                            <span className="font-medium">{warning?.type}</span>
                            <p className="text-base-content/70">{warning?.message}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Payment Info Card */}
          {invoice?.payment_info && (
            <div className="card bg-base-100 shadow-sm">
              <div className="card-body p-4">
                <div className="flex items-center gap-2 mb-4">
                  <div className="p-2 rounded-lg bg-success/10">
                    <PhosphorIcons.CreditCard size={20} weight="bold" className="text-success"/>
                  </div>
                  <h3 className="card-title text-lg">Payment Information</h3>
                </div>

                <div className="space-y-3">
                  {invoice?.payment_info?.terms && (
                    <div className="p-3 bg-base-200 rounded-lg">
                      <h4 className="font-medium mb-2">Payment Terms</h4>
                      <div className="space-y-2 text-sm">
                        <div className="grid grid-cols-2 gap-2">
                          <div>Type: {invoice?.payment_info?.terms?.type}</div>
                          <div>Days: {invoice?.payment_info?.terms?.days}</div>
                        </div>
                        <div>Method: {invoice?.payment_info?.terms?.payment_method}</div>
                        {invoice?.payment_info?.terms?.description && (
                          <div>{invoice?.payment_info?.terms?.description}</div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {invoice?.payment_info?.bank_info && (
                    <div className="p-3 bg-base-200 rounded-lg">
                      <h4 className="font-medium mb-2">Bank Details</h4>
                      <div className="space-y-2 text-sm">
                        <div>Bank: {invoice?.payment_info?.bank_info?.bank}</div>
                        <div>Account Holder: {invoice?.payment_info?.bank_info?.account_holder}</div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>Account: {invoice?.payment_info?.bank_info?.account_number}</div>
                          <div>Routing: {invoice?.payment_info?.bank_info?.routing_number}</div>
                        </div>
                        {invoice?.payment_info?.bank_info?.swift_code && (
                          <div>SWIFT: {invoice?.payment_info?.bank_info?.swift_code}</div>
                        )}
                        {invoice?.payment_info?.bank_info?.iban && (
                          <div>IBAN: {invoice?.payment_info?.bank_info?.iban}</div>
                        )}
                      </div>
                    </div>
                  )}

                  {invoice?.payment_info?.online_payment && (
                    <div className="p-3 bg-base-200 rounded-lg">
                      <h4 className="font-medium mb-2">Online Payment</h4>
                      <div className="space-y-2 text-sm">
                        {invoice?.payment_info?.online_payment?.payment_link && (
                          <a href={invoice?.payment_info?.online_payment?.payment_link} 
                             target="_blank" 
                             rel="noopener noreferrer"
                             className="flex items-center gap-2 text-primary hover:underline">
                            <PhosphorIcons.Link size={16} />
                            Payment Link
                          </a>
                        )}
                        {invoice?.payment_info?.online_payment?.payment_portal && (
                          <div>Portal: {invoice?.payment_info?.online_payment?.payment_portal}</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvoiceExtractorViewResult;